import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {NetworkContext} from '../../../lib/context';
import {ContentSwitcher, DataTable, DataTableSkeleton, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow} from '@carbon/react';
import CustomPagination from '../../../../../components/custom-pagination/CustomPagination';
import NoResultsFound from '../../../../../components/no-results-found';
import {NetworkAdequacyTabViewEnum, type ViewType} from '../useReportsPageController';
import {observer} from 'mobx-react';
import {IFetchReportsPayload} from '../../../lib/types';
import {networkOptimization} from '../../../../../lib/api';
import {providersHeaders} from './network-adequacy-tab-headers/providersHeaders';
import {availableProvidersHeaders} from './network-adequacy-tab-headers/availableProvidersHeaders';
import {adequacyScoresHeaders} from './network-adequacy-tab-headers/adequacyScoresHeaders';
import {NumberFormatter} from '../../../../../lib/formatter';
import {useSorting, DEFAULT_SORT_DIRECTION} from '../../../lib/useSorting';

const PAGE_SIZE = 15;

interface INetworkAdequacyTabContentProps {
    selectedViewType: ViewType;
    onSelectedViewTypeChange: (viewType: ViewType) => void;
    currentViewTypes: ViewType[];
    generateReportButton: React.ReactNode;
}

const initialReportsData = {
    data: {
        providersReport: [] as any[],
        availableProvidersReport: [] as any[],
        adequacyScoresReport: [] as any[],
    },
    totalPages: 1,
    totalItems: 0,
    loading: false
};

const NetworkAdequacyTabContent = (props: INetworkAdequacyTabContentProps) => {

    const {
        selectedViewType, 
        onSelectedViewTypeChange, 
        currentViewTypes,
        generateReportButton
    } = props;

    const noStore = useContext(NetworkContext);
    const [currentPage, setPage] = useState(0);
    const [currentSize, setPageSize] = useState(PAGE_SIZE);
    const [reportsData, setReportsData] = useState(initialReportsData);
    const {sortState, sortRows, resetSort} = useSorting();
    const previousViewTypeIndex = useRef(selectedViewType.index);
    const formatter = new NumberFormatter(0.01,null,true);

    const setDataLoadingState = useCallback((state: boolean) => {
        setReportsData((prevState) => ({
            ...prevState,
            loading: state
        }));
    }, []);

    const fetchReportData = useCallback(async (payload: IFetchReportsPayload, viewType: NetworkAdequacyTabViewEnum) => {
        setDataLoadingState(true);
        try {
            let res;
            let dataKey: 'providersReport' | 'availableProvidersReport' | 'adequacyScoresReport';
            
            switch (viewType) {
            case NetworkAdequacyTabViewEnum.Providers:
                res = await networkOptimization.fetch_providers_report(payload);
                dataKey = 'providersReport';
                break;
            case NetworkAdequacyTabViewEnum.AvailableProviders:
                res = await networkOptimization.fetch_available_providers_report(payload);
                dataKey = 'availableProvidersReport';
                break;
            case NetworkAdequacyTabViewEnum.AdequacyScores:
                res = await networkOptimization.fetch_adequacy_scores(payload);
                dataKey = 'adequacyScoresReport';
                break;
            default:
                return;
            }

            const {data, total_count} = res;
            setReportsData((prevState) => ({
                ...prevState,
                data: {
                    providersReport: [],
                    availableProvidersReport: [],
                    adequacyScoresReport: [],
                    [dataKey]: data
                },
                totalPages: Math.ceil(total_count / (payload.limit || total_count)),
                totalItems: total_count,
            }));
        } catch (error) {
            if (payload.signal.aborted) return;
            console.error('Error fetching report:', error);
        }
        setDataLoadingState(false);
    }, []);

    // Reset page when filters change
    useEffect(() => {
        setPage(0);
    }, [
        noStore.runId,
        noStore.revisionId,
        noStore.appliedGeographyFilter.standard_type,
        noStore.appliedGeographyFilter.standard_version,
        noStore.appliedGeographyFilter.patient_roster_name,
        noStore.appliedGeographyFilter.state,
        noStore.appliedGeographyFilter.county,
        noStore.specialtyFilter,
        selectedViewType.index,
        currentSize,
        sortState
    ]);

    // Reset sort when view type changes (skip initial mount)
    useEffect(() => {
        if (previousViewTypeIndex.current !== selectedViewType.index) {
            resetSort();
            previousViewTypeIndex.current = selectedViewType.index;
        }
    }, [
        selectedViewType.index,
        resetSort,
    ]);

    useEffect(() => {
        const controller = new AbortController();
        const shouldCallApi = [
            noStore.runId,
            noStore.revisionId >= 0 ? true : false,
            noStore.appliedGeographyFilter.standard_type,
            noStore.appliedGeographyFilter.standard_version,
            noStore.appliedGeographyFilter.patient_roster_name,
        ].every(Boolean);
        if (shouldCallApi) {
            const payload: IFetchReportsPayload = {
                roster_id: noStore.runId!,
                revision_id: noStore.revisionId,
                standard_type: noStore.geographyFilters.standard_type,
                standard_version: noStore.geographyFilters.standard_version,
                patient_roster_id: +noStore.geographyFilters.patient_roster_id,
                specialty_code: noStore.specialtyFilterCode ? noStore.specialtyFilterCode.split('|') : [],
                state: noStore.geographyFilters.state ? noStore.geographyFilters.state.split('|') : [],
                county: noStore.geographyFilters.county ? noStore.geographyFilters.county.split('|') : [],
                signal: controller.signal,
                page: currentPage + 1,
                limit: currentSize,
                order_by: sortState
                    ? {
                        direction: sortState.direction,
                        column_name: sortState.column_name,
                    }
                    : undefined,
            };
            fetchReportData(payload, selectedViewType.index);
        } else {
            setReportsData(initialReportsData);
        }
        return () => {
            controller.abort();
        };
    }, [
        noStore.runId,
        noStore.revisionId,
        noStore.appliedGeographyFilter.standard_type,
        noStore.appliedGeographyFilter.standard_version,
        noStore.appliedGeographyFilter.patient_roster_name,
        noStore.appliedGeographyFilter.state,
        noStore.appliedGeographyFilter.county,
        noStore.specialtyFilter,
        selectedViewType.index,
        currentPage,
        currentSize,
        sortState
    ]);

    const headers = useMemo(() => {
        switch (selectedViewType.index) {
        case NetworkAdequacyTabViewEnum.Providers:
            return providersHeaders;
        case NetworkAdequacyTabViewEnum.AvailableProviders:
            return availableProvidersHeaders;
        case NetworkAdequacyTabViewEnum.AdequacyScores:
            return adequacyScoresHeaders;
        default:
            return providersHeaders;
        }
    }, [selectedViewType.index]);

    const currentDataToShow = useMemo(() => {
        let data: any[] = [];
        switch (selectedViewType.index) {
        case NetworkAdequacyTabViewEnum.Providers:
            data = reportsData.data.providersReport;
            break;
        case NetworkAdequacyTabViewEnum.AvailableProviders:
            data = reportsData.data.availableProvidersReport;
            break;
        case NetworkAdequacyTabViewEnum.AdequacyScores:
            data = reportsData.data.adequacyScoresReport;
            break;
        default:
            data = [];
        }
        return data.map((row, index) => ({
            ...row,
            id: `${selectedViewType.index}-${currentPage}-${index}`
        }));
    }, [selectedViewType.index, reportsData.data, currentPage]);

    if (reportsData.loading)
        return <DataTableSkeleton headers={headers} rowCount={PAGE_SIZE} columnCount={headers.length} compact showToolbar={false} />;

    return (
        <DataTable rows={currentDataToShow} headers={headers} isSortable size='sm'>
            {({
                rows,
                headers,
                getHeaderProps,
                getRowProps,
                getTableProps,
                getTableContainerProps
            }) => {
                return (
                    <TableContainer 
                        data-testid='network-adequacy-table-container'
                        title={<div className='mt-[-1.25rem] mb-[-.75rem]'>Network Adequacy</div>}
                        description={<>
                            <div className='mb-4'>Select providers/Score</div>
                            <div className='content-switcher-container'>
                                <ContentSwitcher
                                    className='mb-4 !justify-normal'
                                    data-testid='network-adequacy-content-switcher'
                                    onChange={({index}) => {
                                        onSelectedViewTypeChange(currentViewTypes[index!]);
                                    }}
                                    selectedIndex={selectedViewType.index}
                                    size={'sm'}
                                >
                                    {currentViewTypes.map(viewType => {
                                        return (
                                            <Switch
                                                key={viewType.name + viewType.index + viewType.text}
                                                name={viewType.name}
                                                text={viewType.text}
                                                index={viewType.index}
                                                data-testid={`network-adequacy-switch-${viewType.name.toLowerCase().replace(/\s+/g, '-')}`}
                                            />
                                        );
                                    })}
                                </ContentSwitcher>
                                <div>
                                    {generateReportButton}
                                </div>
                            </div>
                        </>} {...getTableContainerProps()} className='overflow-none'>

                        <Table {...getTableProps()} className='overflow-hidden'>
                            <TableHead>
                                <TableRow>
                                    {headers.map((header: any, i) => {
                                        let headerProps = getHeaderProps({header});
                                        const sortKey = header.sortKey || header.key;
                                        const isSortHeader = sortState?.column_name === sortKey;
                                        const currentSortDirection = isSortHeader && sortState
                                            ? sortState.direction
                                            : DEFAULT_SORT_DIRECTION;

                                        return (
                                            <TableHeader
                                                {...headerProps}
                                                key={i}
                                                isSortHeader={isSortHeader}
                                                sortDirection={currentSortDirection}
                                                onClick={sortRows(sortKey)}
                                            >
                                                {header.header}
                                            </TableHeader>
                                        );
                                    })}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map(row => {
                                    const rowProps = getRowProps({row});
                                    return <TableRow {...rowProps} key={row.id} >
                                        {row.cells.map(cell =>
                                            <TableCell key={cell.id}>
                                                {formatter.formatValue(cell.value)}
                                            </TableCell>
                                        )}
                                    </TableRow>;
                                })}
                            </TableBody>
                        </Table>
                        {rows.length === 0 && (
                            <NoResultsFound data-testid='network-adequacy-no-data' />
                        )}
                        <CustomPagination
                            totalItems={reportsData.totalItems}
                            pageSize={currentSize}
                            page={currentPage}
                            pageSelectorType='Input'
                            onPageSizeChange={(newPageSize: number) => {
                                setPageSize(newPageSize);
                            }}
                            onPageChange={(newPage: number) => {
                                setPage(newPage);
                            }}
                        />
                    </TableContainer>
                );
            }}
        </DataTable>
    );
};

NetworkAdequacyTabContent.displayName = 'NetworkAdequacyTabContent';
export default observer(NetworkAdequacyTabContent);